import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "selectedTags"]
  static values = {
    disabledTags: Array,
    tagsMapping: Object,
    updateUrl: String
  }

  connect() {
    console.log("Risk tag selector controller connected")
    this.searchInput = this.searchTarget
    this.setupEventListeners()
    this.initializeSelectedTags()
    // Don't filter on initial load - show all tags
  }

  disconnect() {
    // Clean up if needed
  }

  initializeSelectedTags() {
    // Initialize pre-selected disabled tags
    if (this.disabledTagsValue && this.disabledTagsValue.length > 0) {
      this.disabledTagsValue.forEach(tag => {
        // Only add the tag pill, don't manipulate checkboxes here
        // The checkboxes should already be checked from the server
        const selectedTagsContainer = this.selectedTagsTarget
        if (!this.element.querySelector(`.tag-pill[data-tag="${tag}"]`)) {
          this.createTagPill(tag)
        }
      })
    }
  }

  setupEventListeners() {
    this.searchInput.addEventListener('input', (e) => this.search(e))
  }

  search(event) {
    const searchTerm = event.target.value.toLowerCase()
    this.filterTags(searchTerm)
  }

  filterTags(searchTerm) {
    console.log("Filtering tags with search term:", searchTerm)
    const tagItems = this.element.querySelectorAll('.tag-item')
    const tagGroups = this.element.querySelectorAll('.tag-group')

    tagGroups.forEach(group => {
      let hasVisibleTags = false
      const groupTags = group.querySelectorAll('.tag-item')

      groupTags.forEach(item => {
        const tagLabel = item.querySelector('.tag-label')
        const tagCheckbox = item.querySelector('.tag-checkbox')
        
        if (tagLabel && tagCheckbox) {
          const labelText = tagLabel.textContent.toLowerCase()
          const tagCode = tagCheckbox.dataset.tag.toLowerCase()
          const isVisible = labelText.includes(searchTerm) || tagCode.includes(searchTerm)
          item.style.display = isVisible ? '' : 'none'
          if (isVisible) hasVisibleTags = true
        }
      })

      group.style.display = hasVisibleTags ? '' : 'none'
    })
  }

  toggleTag(event) {
    const checkbox = event.target
    const tag = checkbox.dataset.tag

    if (checkbox.checked) {
      this.addSelectedTag(tag)
    } else {
      this.removeSelectedTag(tag)
    }
    
    // Update tags via AJAX
    this.updateTagsViaAjax()
  }

  addSelectedTag(tag) {
    if (this.element.querySelector(`.tag-pill[data-tag="${tag}"]`)) {
      return
    }

    this.createTagPill(tag)

    const checkbox = this.element.querySelector(`input[data-tag="${tag}"]`)
    if (checkbox) {
      checkbox.checked = true
    }
  }

  createTagPill(tag) {
    const selectedTagsContainer = this.selectedTagsTarget
    const tagElement = document.createElement('div')
    tagElement.className = 'tag-pill'
    tagElement.dataset.tag = tag

    // Get tag name from mapping or use the tag key itself
    const label = this.tagsMappingValue[tag] || tag

    tagElement.innerHTML = `
      ${label}
      <span class="remove-tag" role="button" tabindex="0">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `

    tagElement.querySelector('.remove-tag').addEventListener('click', () => {
      this.removeSelectedTag(tag)
      this.updateTagsViaAjax()
    })

    selectedTagsContainer.appendChild(tagElement)
  }

  removeSelectedTag(tag) {
    const selectedTag = this.element.querySelector(`.tag-pill[data-tag="${tag}"]`)
    if (selectedTag) {
      selectedTag.remove()
    }

    const checkbox = this.element.querySelector(`input[data-tag="${tag}"]`)
    if (checkbox) {
      checkbox.checked = false
    }
  }

  async updateTagsViaAjax() {
    // Get all selected tags
    const selectedTags = Array.from(this.element.querySelectorAll('.tag-pill'))
      .map(el => el.dataset.tag)

    // Debounce the AJAX call to avoid multiple rapid requests
    clearTimeout(this.updateTimeout)
    this.updateTimeout = setTimeout(async () => {
      try {
        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        
        // Prepare form data
        const formData = new FormData()
        selectedTags.forEach(tag => {
          formData.append('disabled_tags[]', tag)
        })
        
        // Make AJAX request
        const response = await fetch(this.updateUrlValue, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': csrfToken,
            'Accept': 'application/json'
          },
          body: formData
        })

        const data = await response.json()
        
        if (data.success) {
          console.log('Risk tags updated successfully')
          // Optionally show success feedback
          this.showFeedback('success', data.message)
        } else {
          console.error('Failed to update risk tags:', data.errors || data.error)
          this.showFeedback('error', data.errors?.join(', ') || data.error)
        }
      } catch (error) {
        console.error('Error updating risk tags:', error)
        this.showFeedback('error', 'Failed to update risk tags. Please try again.')
      }
    }, 500)
  }

  showFeedback(type, message) {
    // Remove existing feedback
    const existingFeedback = this.element.querySelector('.risk-tag-feedback')
    if (existingFeedback) {
      existingFeedback.remove()
    }

    // Create feedback element
    const feedback = document.createElement('div')
    feedback.className = `risk-tag-feedback risk-tag-feedback--${type}`
    feedback.textContent = message
    
    // Insert feedback after search container
    const searchContainer = this.element.querySelector('.search-container')
    searchContainer.parentNode.insertBefore(feedback, searchContainer.nextSibling)
    
    // Remove feedback after 3 seconds
    setTimeout(() => {
      feedback.remove()
    }, 3000)
  }
}